// --- Dependencies -------------------------------------------------------------------------------
import { IconButton, InputAdornment, TextField } from "@mui/material";
// --- Our Modules --------------------------------------------------------------------------------
import Icon from "@/UI/atoms/Icon";
import "../field.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = { value: string; onChange: (value: string) => void; label?: string; placeholder?: string; onEnter?: () => void };

/**
 * The search field inside the bar's filter mode. It holds its value and nothing else — the bar's
 * apply button is what sends it, so typing never fires a request per keystroke.
 *
 * The same MUI field as every other input, in the bar's shorter variant, so the filter row is not
 * the one place in the app where a box is drawn by hand. The magnifier stays: a label says what the
 * field is *for*, and the icon says what it *does*, which are different jobs.
 */
export default function FilterSearchInput({ value, onChange, label, placeholder, onEnter }: Props) {
    return (
        <TextField
            className="field field--compact filter-search"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && onEnter?.()}
            label={label}
            // Both, unlike every other field: the magnifier is a start adornment, and MUI keeps a
            // label shrunk whenever there is one — so the box is never standing in for the label
            // and the long hint has somewhere to go.
            placeholder={placeholder}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <Icon name="search" size={15} />
                        </InputAdornment>
                    ),
                    endAdornment: value ? (
                        <InputAdornment position="end">
                            <IconButton size="small" onClick={() => onChange("")} aria-label={label ?? placeholder}>
                                <Icon name="close" size={11} />
                            </IconButton>
                        </InputAdornment>
                    ) : undefined,
                },
            }}
            variant="outlined"
        />
    );
}
